import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * Minimal accessible modal primitive (no Radix dependency yet — SPEC-001
 * REQ-065). Traps Escape-to-close, labels the dialog by its title, and
 * returns focus-friendly markup. Used by SPEC-003's create/rename/delete
 * dialogs.
 */
export function Dialog({ open, onClose, title, children, className = '' }: DialogProps) {
  const titleId = useRef(`dialog-title-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center bg-[rgba(3,4,3,0.72)] px-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`w-full max-w-md rounded-xl border border-border bg-surface-2 p-6 shadow-lg ${className}`}
      >
        <h2 id={titleId} className="mb-4 text-lg font-semibold text-text-primary">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
